import React, { useState, useEffect, useMemo } from 'react';
import styles from '../css/DeliveryOptions.module.css';

const DEFAULT_SLOTS = [
  '08:00 - 09:00',
  '09:00 - 10:00',
  '10:00 - 11:00',
  '11:00 - 12:00',
  '12:00 - 13:00',
  '13:00 - 14:00',
  '14:00 - 15:00',
  '15:00 - 16:00',
  '16:00 - 17:00',
];

export default function DeliveryOptions({ mode = 'Delivery', onModeChange, preferredDate, onDateChange, preferredTime, onTimeChange, timeSlots = DEFAULT_SLOTS, readOnly = false, hideUnselected = false, minDate }) {
  const [localDate, setLocalDate] = useState('');
  const [localTime, setLocalTime] = useState(timeSlots[0] || '');

  const todayIso = useMemo(() => {
    try {
      return new Date().toISOString().split('T')[0];
    } catch {
      return undefined;
    }
  }, []);

  const effectiveTimeSlots = useMemo(() => {
    if (!localDate || !todayIso || localDate !== todayIso) {
      return timeSlots;
    }

    const now = new Date();

    return timeSlots.filter((slot) => {
      if (!slot) return false;
      const match = slot.match(/^(\d{2}):(\d{2})/);
      if (!match) return true; // if format unexpected, do not restrict
      const [, hh, mm] = match;
      const slotDate = new Date(now);
      slotDate.setHours(Number(hh) || 0, Number(mm) || 0, 0, 0);
      return slotDate.getTime() >= now.getTime();
    });
  }, [localDate, timeSlots, todayIso]);

  useEffect(() => {
    if (preferredDate) setLocalDate(preferredDate);
  }, [preferredDate]);

  // If a preferredTime is passed in, keep local state in sync
  useEffect(() => {
    if (preferredTime) setLocalTime(preferredTime);
  }, [preferredTime]);

  // On first mount, if the parent has no preferredTime but we have a default slot,
  // propagate that default up so leaving the dropdown as-is still sets a value.
  useEffect(() => {
    if (!preferredTime && effectiveTimeSlots && effectiveTimeSlots.length > 0) {
      const first = effectiveTimeSlots[0];
      setLocalTime(first);
      onTimeChange?.(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the date or available time slots change, ensure the currently
  // selected time is still valid (especially for "today" where earlier
  // slots are filtered out). If not, move to the first available slot.
  useEffect(() => {
    if (!effectiveTimeSlots || effectiveTimeSlots.length === 0) return;
    if (!localTime || !effectiveTimeSlots.includes(localTime)) {
      const first = effectiveTimeSlots[0];
      setLocalTime(first);
      onTimeChange?.(first);
    }
  }, [effectiveTimeSlots, localTime, onTimeChange]);

  return (
    <div className={styles.wrap}>
      <div className={styles.radios}>
        {(!hideUnselected || mode === 'Collect') && (
          <label className={styles.radioLabel}>
            <input type="radio" name="mode" checked={mode === 'Collect'} disabled={readOnly} onChange={readOnly ? undefined : () => onModeChange?.('Collect')} />
            Collect
          </label>
        )}
        {(!hideUnselected || mode === 'Delivery') && (
          <label className={styles.radioLabel}>
            <input type="radio" name="mode" checked={mode === 'Delivery'} disabled={readOnly} onChange={readOnly ? undefined : () => onModeChange?.('Delivery')} />
            Delivery
          </label>
        )}
      </div>
      <div className={styles.deliveryPanel}>
        <label className={styles.pickerLabel} htmlFor="preferredDate">Preferred Date</label>
        <input
          id="preferredDate"
          type="date"
          className={`${styles.picker} ${styles.compact}`}
          value={localDate}
          min={minDate}
          disabled={readOnly}
          onChange={readOnly ? undefined : (e) => {
            setLocalDate(e.target.value);
            onDateChange?.(e.target.value);
          }}
        />
        <label className={styles.pickerLabel} htmlFor="preferredTime">Preferred Time</label>
        <select
          id="preferredTime"
          className={`${styles.picker} ${styles.compact}`}
          value={localTime}
          disabled={readOnly || !effectiveTimeSlots || effectiveTimeSlots.length === 0}
          onChange={readOnly ? undefined : (e) => {
            setLocalTime(e.target.value);
            onTimeChange?.(e.target.value);
          }}
        >
          {effectiveTimeSlots.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
